import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox'
import { CheckIcon, ChevronDownIcon } from '@/components/ui/icons'
import { cn } from '@/helpers/utils'

const Combobox = ComboboxPrimitive.Root

function ComboboxTrigger({
	className,
	size = 'default',
	children,
	...props
}: ComboboxPrimitive.Trigger.Props & {
	size?: 'sm' | 'default'
}) {
	return (
		<ComboboxPrimitive.Trigger
			className={cn(
				'flex w-fit cursor-pointer items-center justify-between gap-2 rounded-lg border border-neutral-300 bg-neutral-white px-3 font-normal transition-all duration-150',
				'hover:not-disabled:bg-neutral-50',
				'hover:not-disabled:shadow-focus-primary-100-xxs hover:not-disabled:outline hover:not-disabled:outline-1 hover:not-disabled:outline-neutral-300',
				'focus:shadow-focus-primary-100-xxs',
				'data-[popup-open]:shadow-focus-primary-100-xxs data-[popup-open]:outline data-[popup-open]:outline-1 data-[popup-open]:outline-neutral-300',
				'disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:opacity-50',
				size === 'sm' ? 'h-10 text-sm' : 'h-11 text-base',
				className
			)}
			data-size={size}
			data-slot='combobox-trigger'
			{...props}
		>
			{children}
			<ComboboxPrimitive.Icon className='ml-auto text-neutral-500'>
				<ChevronDownIcon
					className='in-data-popup-open:rotate-180 transition-transform duration-150'
					size={16}
				/>
			</ComboboxPrimitive.Icon>
		</ComboboxPrimitive.Trigger>
	)
}

function ComboboxInput({ className, ...props }: ComboboxPrimitive.Input.Props) {
	return (
		<ComboboxPrimitive.Input
			className={cn(
				'mb-2 w-full rounded-md border border-neutral-200 bg-neutral-white px-3 py-2 text-md outline-none placeholder:text-neutral-400 focus:border-neutral-300 focus:shadow-focus-primary-100-xxs',
				className
			)}
			data-slot='combobox-input'
			{...props}
		/>
	)
}

function ComboboxContent({
	className,
	children,
	side = 'bottom',
	sideOffset = 4,
	align = 'start',
	alignOffset = 0,
	container,
	...props
}: ComboboxPrimitive.Popup.Props &
	Pick<
		ComboboxPrimitive.Positioner.Props,
		'side' | 'align' | 'sideOffset' | 'alignOffset'
	> &
	Pick<ComboboxPrimitive.Portal.Props, 'container'>) {
	return (
		// `container` keeps the popup inside a modal that manages its own layer.
		// A drawer or dialog disables pointer events on the body and re-enables
		// them only within its own subtree, so a popup portalled to the body
		// lands outside it: taps and scrolls never reach the list, and the ones
		// that do read as interactions with the background and dismiss the modal.
		// Portalling into the modal itself sidesteps all of that. Defaults to the
		// body, which is right everywhere else.
		<ComboboxPrimitive.Portal container={container}>
			<ComboboxPrimitive.Positioner
				align={align}
				alignOffset={alignOffset}
				className='z-(--z-dropdown)'
				side={side}
				sideOffset={sideOffset}
			>
				<ComboboxPrimitive.Popup
					className={cn(
						'z-(--z-dropdown) overflow-hidden rounded-lg border border-neutral-200 bg-neutral-white p-1 px-1.5 shadow-lg',
						'origin-(--transform-origin) transition-[transform,scale,opacity] data-ending-style:scale-95 data-starting-style:scale-95 data-ending-style:opacity-0 data-starting-style:opacity-0',
						className
					)}
					data-slot='combobox-content'
					{...props}
				>
					{children}
				</ComboboxPrimitive.Popup>
			</ComboboxPrimitive.Positioner>
		</ComboboxPrimitive.Portal>
	)
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
	return (
		<ComboboxPrimitive.List
			className={cn(
				'max-h-[200px] overflow-y-auto [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-neutral-50',
				className
			)}
			data-slot='combobox-list'
			{...props}
		/>
	)
}

function ComboboxItem({
	className,
	children,
	...props
}: ComboboxPrimitive.Item.Props) {
	return (
		<ComboboxPrimitive.Item
			className={cn(
				'my-px flex cursor-pointer items-center overflow-x-hidden truncate rounded-md px-2 py-2.5 text-neutral-900 text-sm transition-all duration-150',
				'data-[disabled]:pointer-events-none data-[disabled]:text-neutral-400 data-[disabled]:opacity-50',
				'data-[highlighted]:bg-neutral-100',
				className
			)}
			data-slot='combobox-item'
			{...props}
		>
			{children}
			<ComboboxPrimitive.ItemIndicator
				render={
					<span className='pointer-events-none ml-auto flex size-4 shrink-0 items-center justify-center' />
				}
			>
				<CheckIcon size={16} />
			</ComboboxPrimitive.ItemIndicator>
		</ComboboxPrimitive.Item>
	)
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
	return (
		<ComboboxPrimitive.Group
			className={cn(className)}
			data-slot='combobox-group'
			{...props}
		/>
	)
}

function ComboboxLabel({
	className,
	...props
}: ComboboxPrimitive.GroupLabel.Props) {
	return (
		<ComboboxPrimitive.GroupLabel
			className={cn(
				'px-2 py-1.5 font-medium text-neutral-500 text-xs',
				className
			)}
			data-slot='combobox-label'
			{...props}
		/>
	)
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
	return (
		<ComboboxPrimitive.Empty
			className={cn(
				'p-2 text-center text-neutral-500 text-sm',
				className
			)}
			data-slot='combobox-empty'
			{...props}
		/>
	)
}

function ComboboxSeparator({
	className,
	...props
}: ComboboxPrimitive.Separator.Props) {
	return (
		<ComboboxPrimitive.Separator
			className={cn(
				'pointer-events-none -mx-1 my-1 h-px bg-neutral-200',
				className
			)}
			data-slot='combobox-separator'
			{...props}
		/>
	)
}

export {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxInput,
	ComboboxItem,
	ComboboxLabel,
	ComboboxList,
	ComboboxSeparator,
	ComboboxTrigger
}

// Re-export primitives for advanced use cases (custom triggers, etc.)
const ComboboxComponents = ComboboxPrimitive
export { ComboboxComponents }
export type { Combobox as ComboboxComponentsNamespace } from '@base-ui/react/combobox'
